// Importing necessary dependencies
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import Http from "../services/Http";
import { toastHandler } from "../../utils/utils";
import { messages } from "../../utils/messages";

// Initial state for the organization slice
const initialState = {
  organizationData: [],
  status: "idle",
};

// Destructuring Http function from the Http service
const { httpRequest } = Http();

// Async thunk to fetch organization list
export const organizationList = createAsyncThunk(
  "getOrganization",
  async (_, { rejectWithValue }) => {
    try {
      const response = await httpRequest(
        {
          method: "GET",
          url: "me",
        },
        _.navigate
      );
      return response;
    } catch (err) {
      return rejectWithValue(err.message || "An error occurred", {
        cause: err.cause,
      });
    }
  }
);

// Async thunk to update organization
export const updateOrganization = createAsyncThunk(
  "updateOrganization",
  async ({ orgName, logo, webSiteName, navigate }, { rejectWithValue }) => {
    try {
      return await httpRequest(
        {
          method: "PUT",
          url: "admin/organisation",
          body: { name: orgName, logo: logo, website: webSiteName },
        },
        navigate
      );
    } catch (err) {
      return rejectWithValue(err.message || "An error occurred", {
        cause: err.cause,
      });
    }
  }
);

// Slice for managing organization state
const organizationSlice = createSlice({
  name: "organization",
  initialState,
  reducers: {
    // Reducer to show toast message
    showToast: (state, action) => {
      const { success, message } = action.payload;
      toastHandler(success, message);
    },
    setorganizationData: (state, action) => {
      state.organizationData = action.payload.data.user;
    },
  },
  extraReducers: (builder) => {
    builder
      // Reducer to handle pending actions
      .addMatcher(
        (action) =>
          [organizationList.pending, updateOrganization.pending].some((type) =>
            type.match(action)
          ),
        (state, action) => {
          state.status = "loading";
          action.meta.arg.setLoader(true);
        }
      )
      // Reducer to handle fulfilled actions
      .addMatcher(
        (action) =>
          [organizationList.fulfilled, updateOrganization.fulfilled].some(
            (type) => type.match(action)
          ),
        (state, action) => {
          state.status = "succeeded";
          action.meta.arg.setLoader(false);
          if (["getOrganization"].includes(action.type.split("/")[0]))
            state.organizationData = action.payload.user;
          if (["updateOrganization"].includes(action.type.split("/")[0])) {
            state.organizationData.organisation = action.payload.orgnisation;
            // Dispatching showToast action
            organizationSlice.caseReducers.showToast(state, {
              payload: {
                success: true,
                message:
                  action?.payload?.message ||
                  messages[action.type.split("/")[0]].success,
              },
            });
          }
        }
      )
      // Reducer to handle rejected actions
      .addMatcher(
        (action) =>
          [organizationList.rejected, updateOrganization.rejected].some(
            (type) => type.match(action)
          ),
        (state, action) => {
          state.status = "failed";
          // Dispatching showToast action
          organizationSlice.caseReducers.showToast(state, {
            payload: {
              success: false,
              message: action.payload,
            },
          });
          action.meta.arg.setLoader(false);
        }
      );
  },
});

// Action creators are generated for each case reducer function
export const { setorganizationData } = organizationSlice.actions;

// Exporting reducer
export default organizationSlice.reducer;